import { useInfiniteQuery } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { flattenInfinitePages, getNextPageParam } from '@/lib/constants/pagination';
import { getProjects } from '@/modules/projects';
import { mapProjectBillingModelToDTO } from '@/modules/projects/mappers/project-billing-model.mapper';
import { mapProjectStatusToDTO } from '@/modules/projects/mappers/project-status.mapper';
import { ProjectBillingModel, ProjectStatus } from '@/modules/projects/types/project';
import { RangeMode } from '@/types/filters';

export const useProjects = ({ filters }: { filters: FilterValues['values']; sorting?: any }) => {
  return useInfiniteQuery({
    queryKey: ['projects', filters],
    queryFn: ({ pageParam }) => {
      const apiFilters = mapProjectFilters(filters);
      return getProjects({ cursor: pageParam, filters: apiFilters });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    staleTime: 10 * 10 * 1000, // 10 minutes
    select: flattenInfinitePages,
  });
};

const mapProjectFilters = (filters: FilterValues['values']) => {
  const apiFilters: Record<string, any> = {};

  if (filters.searchString) {
    apiFilters.searchString = filters.searchString;
  }

  if (filters.status) {
    const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
    apiFilters.status = statusArray.map((s: ProjectStatus) => mapProjectStatusToDTO(s));
  }

  if (filters.billingModel) {
    const billingArray = Array.isArray(filters.billingModel)
      ? filters.billingModel
      : [filters.billingModel];
    apiFilters.billingModel = billingArray.map((b: ProjectBillingModel) =>
      mapProjectBillingModelToDTO(b)
    );
  }

  if (filters.probability && typeof filters.probability === 'string') {
    const [mode, value] = filters.probability.split(':');
    if (mode === RangeMode.More && value) {
      apiFilters.tentativeProbabilityMin = Number(value) / 100;
    } else if (mode === RangeMode.Less && value) {
      apiFilters.tentativeProbabilityMax = Number(value) / 100;
    }
  }

  if (filters.manager) {
    const managerArray = Array.isArray(filters.manager) ? filters.manager : [filters.manager];
    apiFilters.managerId = managerArray.map((id: string | number) => Number(id));
  }

  return apiFilters;
};
